import { type ReactNode } from 'react';
import { useRoleCheck, type RolesConfig } from '@/hooks/useRoleCheck';

export interface RoleBasedRenderProps {
  /** Role requirements configuration */
  roles?: RolesConfig;
  /** Optional explicit organization ID (overrides route params) */
  organizationId?: string;
  /** Content to render if user has access */
  children: ReactNode;
  /** Optional fallback content when access is denied */
  fallback?: ReactNode;
  /** Optional loading content while checking roles */
  loading?: ReactNode;
  /** If true, show fallback content when access is denied. If false, hide completely (default behavior). */
  showFallback?: boolean;
}

/**
 * Conditional render wrapper based on user roles
 *
 * Use this to conditionally show/hide UI elements like buttons, tabs, menu items
 * based on user's roles. Supports both global roles and organization-scoped roles.
 *
 * @example
 * // Global role check - show button only for users
 * <RoleBasedRender roles={{ anyOf: ['user'] }}>
 *   <Button>Create Organization</Button>
 * </RoleBasedRender>
 *
 * @example
 * // Organization role check - show settings tab only for owners/managers
 * <RoleBasedRender roles={{ param: 'organizationId', anyOf: ['owner', 'manager'] }}>
 *   <NavigationTab value="settings">Settings</NavigationTab>
 * </RoleBasedRender>
 *
 * @example
 * // With fallback content
 * <RoleBasedRender
 *   roles={{ param: 'organizationId', anyOf: ['owner'] }}
 *   fallback={<Button disabled>Settings (Owner only)</Button>}
 * >
 *   <Button>Settings</Button>
 * </RoleBasedRender>
 *
 * @example
 * // With explicit organization ID
 * <RoleBasedRender
 *   roles={{ anyOf: ['owner', 'manager'] }}
 *   organizationId={currentOrgId}
 * >
 *   <MenuItem>Manage Members</MenuItem>
 * </RoleBasedRender>
 *
 * @example
 * // Multiple role checks for different elements
 * <div>
 *   <RoleBasedRender roles={{ param: 'organizationId', anyOf: ['member', 'manager', 'owner'] }}>
 *     <ViewButton />
 *   </RoleBasedRender>
 *
 *   <RoleBasedRender roles={{ param: 'organizationId', anyOf: ['manager', 'owner'] }}>
 *     <EditButton />
 *   </RoleBasedRender>
 *
 *   <RoleBasedRender roles={{ param: 'organizationId', anyOf: ['owner'] }}>
 *     <DeleteButton />
 *   </RoleBasedRender>
 * </div>
 */
export function RoleBasedRender({
  roles,
  organizationId,
  children,
  fallback = null,
  loading = null,
  showFallback = false,
}: RoleBasedRenderProps) {
  const { hasAccess, isLoading } = useRoleCheck(roles, organizationId);

  if (isLoading) {
    return <>{loading}</>;
  }

  if (!hasAccess) {
    return showFallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
}
